import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle, Loader2, AlertTriangle, ShieldCheck, Facebook, Mail } from 'lucide-react';
import BrandHero from '../components/BrandHero';
import { Seo } from '../components/academy/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

const ToolkitAccess = () => {
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | paid | pending | error | notfound
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState('notfound');
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/toolkit/status/${sessionId}`);
        const { paymentStatus, token: tk } = res.data || {};
        if (paymentStatus === 'paid') {
          setToken(tk || null);
          setState('paid');
          return;
        }
        if (paymentStatus === 'expired' || attempts >= MAX_POLLS) {
          setState(paymentStatus === 'expired' ? 'error' : 'pending');
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= MAX_POLLS) {
          setState('error');
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const downloadUrl = token ? `${API}/toolkit/download/${token}` : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="toolkit-access-page">
      <Seo
        siteName="CarLens"
        title="Your CarLens Toolkit"
        description="Download your CarLens Ultimate Used Car Buyer Toolkit."
        path="/toolkit-access"
        noindex
      />

      <BrandHero />

      <section className="max-w-2xl mx-auto px-6 py-20 md:py-28 text-center">
        {state === 'checking' && (
          <div data-testid="toolkit-access-checking">
            <Loader2 className="w-12 h-12 text-orange-400 mx-auto animate-spin mb-6" />
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Confirming your purchase&hellip;</h1>
            <p className="mt-3 text-zinc-400">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === 'paid' && (
          <div data-testid="toolkit-access-paid">
            <div className="w-20 h-20 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-11 h-11 text-orange-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Your Toolkit Is Ready!</h1>
            <p className="mt-4 text-zinc-300 text-lg leading-relaxed">
              Thank you for your purchase. Your <strong className="text-white">CarLens Ultimate Used Car Buyer Toolkit&trade;</strong> is ready to download.
            </p>

            {downloadUrl ? (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="toolkit-download-link">
                <Button
                  size="lg"
                  className="mt-8 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20"
                  data-testid="toolkit-download-btn"
                >
                  <Download className="w-5 h-5 mr-2" /> Download Your Toolkit
                </Button>
              </a>
            ) : (
              <p className="mt-8 text-zinc-400">Your download link is being prepared. Please refresh this page in a moment.</p>
            )}

            <div className="mt-8 rounded-2xl border border-zinc-800 bg-[#161618] p-6 text-left max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <p className="text-zinc-300 text-sm leading-relaxed">
                  If you entered your email at checkout, we&rsquo;ve also sent your private download link there so you can access the toolkit anytime.
                </p>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-orange-600/10 p-6 max-w-lg mx-auto">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-orange-400" />
                <span className="text-white font-semibold">Found a car you&rsquo;re serious about?</span>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-4">
                Let CarLens take the guesswork out completely with a professional consultation or full pre-purchase inspection.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10" onClick={() => navigate('/firstlook')} data-testid="toolkit-access-firstlook-btn">
                  CarLens FirstLook
                </Button>
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-white/5" onClick={() => navigate('/book')} data-testid="toolkit-access-book-btn">
                  Book an Inspection
                </Button>
              </div>
            </div>
          </div>
        )}

        {state === 'pending' && (
          <div data-testid="toolkit-access-pending">
            <Loader2 className="w-12 h-12 text-orange-400 mx-auto animate-spin mb-6" />
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Still confirming your payment&hellip;</h1>
            <p className="mt-3 text-zinc-400">This is taking a little longer than usual. If you completed payment, refresh this page in a moment &mdash; and check your email for your download link.</p>
            <Button className="mt-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white" onClick={() => window.location.reload()} data-testid="toolkit-access-refresh-btn">
              Refresh
            </Button>
          </div>
        )}

        {(state === 'error' || state === 'notfound') && (
          <div data-testid="toolkit-access-error">
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {state === 'notfound' ? 'No purchase found' : 'We couldn\u2019t confirm your purchase'}
            </h1>
            <p className="mt-3 text-zinc-400">
              {state === 'notfound'
                ? 'It looks like you arrived here without completing a checkout.'
                : 'If you were charged, don\u2019t worry \u2014 check your email for your download link, or contact us and we\u2019ll sort it out right away.'}
            </p>
            <Button className="mt-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white" onClick={() => navigate('/used-car-buyer-toolkit')} data-testid="toolkit-access-retry-btn">
              Back to the Toolkit
            </Button>
          </div>
        )}
      </section>

      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold tracking-wide" style={{ fontFamily: 'Space Grotesk' }}>CarLens Automotive Intelligence</p>
              <p className="text-zinc-500 text-sm">&copy; 2025 CarLens Automotive Intelligence. All rights reserved.</p>
            </div>
            <Link to="/used-car-buyer-toolkit" className="text-orange-400 hover:text-orange-300 text-sm font-medium">View the Toolkit</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolkitAccess;
